import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { customersAPI, freeInvoicesAPI } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Receipt,
  Save,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";

export default function FreeInvoiceCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: "",
    title: "Freitext-Rechnung",
    payment_days: 14,
    include_vat: true,
    vat_rate: 19,
    notes: "",
    show_item_prices: true
  });

  // Optionale Ausführungsadresse (Leistungsort). Wenn leer, wird kein zweiter
  // Adressblock im PDF gerendert.
  const [executionAddress, setExecutionAddress] = useState({
    name: "",
    street: "",
    zip: "",
    city: "",
  });
  const _hasExecutionAddress = () =>
    !!(executionAddress.name.trim() || executionAddress.street.trim() ||
       executionAddress.zip.trim() || executionAddress.city.trim());
  
  const [items, setItems] = useState([
    { description: "", quantity: 1, unit: "Stück", unit_price: 0 }
  ]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersRes] = await Promise.all([
        customersAPI.list()
      ]);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, unit: "Stück", unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (item.quantity * item.unit_price);
    }, 0);
    const vatAmount = formData.include_vat ? subtotal * (formData.vat_rate / 100) : 0;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error("Bitte wählen Sie einen Kunden aus");
      return;
    }
    
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error("Bitte füllen Sie alle Positionen aus (Beschreibung und Preis)");
      return;
    }
    
    setSaving(true);
    try {
      const response = await freeInvoicesAPI.create({
        ...formData,
        items: items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        execution_address: _hasExecutionAddress() ? {
          name: executionAddress.name.trim(),
          street: executionAddress.street.trim(),
          zip: executionAddress.zip.trim(),
          city: executionAddress.city.trim(),
        } : null,
      });
      
      toast.success(`Freitext-Rechnung ${response.data.invoice_number} erstellt`);
      navigate(`/freitext-rechnungen/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create invoice:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Rechnung");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const { subtotal, vatAmount, total } = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">Freitext-Rechnung erstellen</h1>
          <p className="text-muted-foreground">Erstellen Sie eine Rechnung mit freien Positionen</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rechnungsempfänger</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Kunde auswählen *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                >
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.first_name} {customer.last_name}
                        {customer.company_name && ` (${customer.company_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Titel / Betreff</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="z.B. Wartungsarbeiten Mai 2026"
                />
              </div>
            </CardContent>
          </Card>

          {/* Ausführungsadresse (optional, für abweichenden Leistungsort) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ausführungsadresse <span className="text-xs text-muted-foreground font-normal">(optional – nur bei abweichendem Leistungsort)</span></CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Wenn die Leistung an einer anderen Adresse erbracht wurde als die
                Rechnungsanschrift (z.&nbsp;B. bei Baustellen, Zweitwohnungen), können Sie
                hier den Leistungsort eintragen. Erscheint im PDF neben der Rechnungsanschrift.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Firma / Name</Label>
                  <Input
                    value={executionAddress.name}
                    onChange={(e) => setExecutionAddress({ ...executionAddress, name: e.target.value })}
                    placeholder="z.B. Baustelle Musterstraße"
                    data-testid="execution-address-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Straße & Hausnr.</Label>
                  <Input
                    value={executionAddress.street}
                    onChange={(e) => setExecutionAddress({ ...executionAddress, street: e.target.value })}
                    placeholder="Musterstraße 12"
                    data-testid="execution-address-street"
                  />
                </div>
                <div className="space-y-2">
                  <Label>PLZ</Label>
                  <Input
                    value={executionAddress.zip}
                    onChange={(e) => setExecutionAddress({ ...executionAddress, zip: e.target.value })}
                    placeholder="12345"
                    data-testid="execution-address-zip"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ort</Label>
                  <Input
                    value={executionAddress.city}
                    onChange={(e) => setExecutionAddress({ ...executionAddress, city: e.target.value })}
                    placeholder="Musterstadt"
                    data-testid="execution-address-city"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Rechnungspositionen
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Position hinzufügen
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-4">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium text-muted-foreground">
                      Position {index + 1}
                    </span>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Beschreibung *</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => updateItem(index, "description", e.target.value)}
                      placeholder="Beschreibung der Leistung/des Artikels"
                      rows={2}
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Menge</Label>
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, "quantity", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Einheit</Label>
                      <Select
                        value={item.unit}
                        onValueChange={(value) => updateItem(index, "unit", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Stück">Stück</SelectItem>
                          <SelectItem value="Stunden">Stunden</SelectItem>
                          <SelectItem value="Pauschal">Pauschal</SelectItem>
                          <SelectItem value="m²">m²</SelectItem>
                          <SelectItem value="lfm">lfm</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Einzelpreis (€) *</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="text-right text-sm">
                    <span className="text-muted-foreground">Summe: </span>
                    <span className="font-medium">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Payment & Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Zahlungsbedingungen & Anmerkungen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Zahlungsziel (Tage)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.payment_days}
                    onChange={(e) => setFormData({ ...formData, payment_days: parseInt(e.target.value) || 14 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>MwSt.-Satz (%)</Label>
                  <Select
                    value={String(formData.vat_rate)}
                    onValueChange={(value) => setFormData({ ...formData, vat_rate: parseFloat(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="19">19%</SelectItem>
                      <SelectItem value="7">7%</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Label>Mit Mehrwertsteuer</Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.include_vat ? "MwSt. wird ausgewiesen" : "Netto ohne MwSt. (§ 19 UStG)"}
                  </p>
                </div>
                <Switch
                  checked={formData.include_vat}
                  onCheckedChange={(checked) => setFormData({ ...formData, include_vat: checked })}
                />
              </div>

              <div className="flex items-start justify-between p-3 bg-muted rounded-lg gap-3">
                <div>
                  <Label>Einzelpreise im PDF ausweisen</Label>
                  <p className="text-xs text-muted-foreground">
                    Aus → Im PDF erscheinen nur Beschreibung, Menge und Einheit. Die Gesamtsumme steht am Ende.
                  </p>
                </div>
                <Switch
                  checked={formData.show_item_prices}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_item_prices: checked })}
                  data-testid="show-item-prices-switch"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Anmerkungen (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Zusätzliche Hinweise für den Kunden..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Price Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Rechnungsbetrag</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Nettobetrag</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                
                {formData.include_vat && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>MwSt. ({formData.vat_rate}%)</span>
                    <span>{formatCurrency(vatAmount)}</span>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex justify-between items-center">
                <span className="font-semibold">
                  {formData.include_vat ? "Bruttobetrag" : "Nettobetrag"}
                </span>
                <span className="text-2xl font-bold font-['Manrope'] text-accent">
                  {formatCurrency(total)}
                </span>
              </div>

              <Separator />

              <Button 
                type="submit" 
                className="w-full" 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Rechnung erstellen
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                Die Rechnung wird als Entwurf erstellt
              </p>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
